'use client';

import React, { useEffect, useState, useMemo } from 'react';
import { CanLogoIcon } from '../../../../../public/assets/icons';
import { signupStyles } from '../signup-templates/signup.styles';
import { Box, Stack, Text, Button, Field, Select, Portal, Spinner, createListCollection, Flex } from '@chakra-ui/react';
import { SelectValueChangeDetails } from '@chakra-ui/react';
import { AiOutlineClose } from 'react-icons/ai';
import { PaymentProps } from './types/template.types';

// Define Pokémon Type
type Pokemon = {
   name: string;
};

// Fetch Pokémon Function
const fetchPokemonList = async (): Promise<Pokemon[]> => {
   try {
      const response = await fetch('https://pokeapi.co/api/v2/pokemon?limit=10');
      const data = await response.json();
      return data.results || [];
   } catch (error) {
      console.error('Error fetching Pokémon:', error);
      return [];
   }
};

const Survey = ({ previous }: PaymentProps) => {
   const [pokemonList, setPokemonList] = useState<Pokemon[]>([]);
   const [loading, setLoading] = useState(true);

   useEffect(() => {
      const loadPokemon = async () => {
         setLoading(true);
         const pokemons = await fetchPokemonList();
         setPokemonList(pokemons);
         setLoading(false);
      };
      loadPokemon();
   }, []);

   // Create a list collection for Select component
   const collection = useMemo(() => {
      return createListCollection({
         items: pokemonList.map(pokemon => pokemon.name), // Convert to string[]
         itemToString: name => name, // Already a string
         itemToValue: name => name, // Already a string
      });
   }, [pokemonList]);

   // Handle selection
   const [selectedPokemons, setSelectedPokemons] = useState<string[]>([]);
   const handleSelection = (details: SelectValueChangeDetails<string>) => {
      setSelectedPokemons(details.value); // `details.value` is already an array of strings
   };

   // Remove selected Pokémon
   const handleRemovePokemon = (pokemonNameToRemove: string) => {
      setSelectedPokemons(prevSelected => prevSelected.filter(pokemonName => pokemonName !== pokemonNameToRemove));
   };

   const [methodSelectedOption, setMethodSelectedOption] = useState('');
   const [medicineSelectedOption, setMedicineSelectedOption] = useState('');

   const options = [
      { id: 'methodYes', label: 'Yes' },
      { id: 'methodNo', label: 'No' },
      { id: 'methodSometimes', label: 'Sometimes' },
   ];
   const options2 = [
      { id: 'medicineYes', label: 'Yes' },
      { id: 'medicineNo', label: 'No' },
      { id: 'medicineSupport', label: 'I support this for other but I wouldn’t choose to grow' },
   ];

   return (
      <>
         <Box minH="72px">
            <CanLogoIcon />
         </Box>
         <Text as="h2" color="white" fontSize={{ base: '4xl', sm: '5xl', md: '12xl' }} fontWeight="bold">
            CANCARD<sup style={{ fontSize: '0.7em', fontWeight: '400' }}>™</sup> SURVEY
         </Text>
         <Text color="white" textStyle="md" textAlign="center" mt="-10px" mb="2">
            Please enter your personal details.
            <br /> Your data is secure.
         </Text>
         <Box as="form" css={signupStyles.signup_form}>
            <Stack gap="4" align="flex-start" minW="full">
               <Field.Root>
                  <Field.Label textStyle="xs" color="white" mb="1">
                     What conditions do you find cannabis helpful for?
                  </Field.Label>
                  <Box css={signupStyles.multiselect_box_con}>
                     {/* Multi-select dropdown */}
                     <Select.Root
                        collection={collection}
                        height="full"
                        width="full"
                        position="absolute"
                        top="0"
                        left="0"
                        zIndex="9"
                        multiple
                        onValueChange={handleSelection}
                        value={selectedPokemons}
                     >
                        <Select.HiddenSelect />
                        <Select.Control style={{ minHeight: '100%' }}>
                           <Select.Trigger style={{ height: '100%', opacity: 0 }}>
                              <Select.ValueText placeholder="Select Pokémon" />
                           </Select.Trigger>
                           <Select.IndicatorGroup style={{ height: '100%' }}>
                              {loading && <Spinner size="xs" borderWidth="1.5px" color="fg.muted" />}
                              <Select.Indicator />
                           </Select.IndicatorGroup>
                        </Select.Control>
                        <Portal>
                           <Select.Positioner css={signupStyles.multiselect_box_dropdown_con}>
                              <Select.Content>
                                 {collection.items.map(pokemonName => (
                                    <Select.Item key={pokemonName} item={pokemonName}>
                                       {pokemonName} {/* Directly use pokemonName (it's already a string) */}
                                       <Select.ItemIndicator />
                                    </Select.Item>
                                 ))}
                              </Select.Content>
                           </Select.Positioner>
                        </Portal>
                     </Select.Root>

                     {/* Display selected Pokémon as tags */}
                     <Box css={signupStyles.multiselect_box}>
                        {selectedPokemons.length === 0 ? (
                           <span style={{ color: 'white', fontSize: '14px', paddingLeft: '5px', paddingTop: '3px' }}>
                              Select
                           </span>
                        ) : (
                           selectedPokemons.map(pokemonName => (
                              <span
                                 key={pokemonName}
                                 style={{
                                    background: 'darkGrey',
                                    borderRadius: '12px',
                                    fontSize: '12px',
                                    padding: '2px 5px',
                                    color: 'black',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '2px',
                                 }}
                              >
                                 {pokemonName}
                                 <button
                                    onClick={() => handleRemovePokemon(pokemonName)}
                                    style={{ zIndex: 99, cursor: 'pointer' }}
                                 >
                                    <AiOutlineClose />
                                 </button>
                              </span>
                           ))
                        )}
                     </Box>
                  </Box>
               </Field.Root>
               <Field.Root>
                  <Field.Label textStyle="xs" color="white" mb="1">
                     What strains would you recommend to others who have a similar condition
                  </Field.Label>
                  <Box css={signupStyles.multiselect_box_con}>
                     {/* Multi-select dropdown */}
                     <Select.Root
                        collection={collection}
                        height="full"
                        width="full"
                        position="absolute"
                        top="0"
                        left="0"
                        zIndex="9"
                        multiple
                        onValueChange={handleSelection}
                        value={selectedPokemons}
                     >
                        <Select.HiddenSelect />
                        <Select.Control style={{ minHeight: '100%' }}>
                           <Select.Trigger style={{ height: '100%', opacity: 0 }}>
                              <Select.ValueText placeholder="Select Pokémon" />
                           </Select.Trigger>
                           <Select.IndicatorGroup style={{ height: '100%' }}>
                              {loading && <Spinner size="xs" borderWidth="1.5px" color="fg.muted" />}
                              <Select.Indicator />
                           </Select.IndicatorGroup>
                        </Select.Control>
                        <Portal>
                           <Select.Positioner css={signupStyles.multiselect_box_dropdown_con}>
                              <Select.Content>
                                 {collection.items.map(pokemonName => (
                                    <Select.Item key={pokemonName} item={pokemonName}>
                                       {pokemonName} {/* Directly use pokemonName (it's already a string) */}
                                       <Select.ItemIndicator />
                                    </Select.Item>
                                 ))}
                              </Select.Content>
                           </Select.Positioner>
                        </Portal>
                     </Select.Root>

                     {/* Display selected Pokémon as tags */}
                     <Box css={signupStyles.multiselect_box}>
                        {selectedPokemons.length === 0 ? (
                           <span style={{ color: 'white', fontSize: '14px', paddingLeft: '5px', paddingTop: '3px' }}>
                              Select
                           </span>
                        ) : (
                           selectedPokemons.map(pokemonName => (
                              <span
                                 key={pokemonName}
                                 style={{
                                    background: 'darkGrey',
                                    borderRadius: '12px',
                                    fontSize: '12px',
                                    padding: '2px 5px',
                                    color: 'black',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '2px',
                                 }}
                              >
                                 {pokemonName}
                                 <button
                                    onClick={() => handleRemovePokemon(pokemonName)}
                                    style={{ zIndex: 99, cursor: 'pointer' }}
                                 >
                                    <AiOutlineClose />
                                 </button>
                              </span>
                           ))
                        )}
                     </Box>
                  </Box>
               </Field.Root>
               <Field.Root>
                  <Field.Label textStyle="xs" color="white" mb="1">
                     What is the best consumption method for you?
                  </Field.Label>
                  <Flex justify="start" flexWrap="wrap" gap="15px">
                     {options.map(option => (
                        <Box
                           key={option.id}
                           cursor="pointer"
                           borderRadius="12px"
                           minW="130px"
                           h="10"
                           px="6"
                           display="flex"
                           alignItems="center"
                           justifyContent="center"
                           textAlign="center"
                           transition="all 0.3s ease-in-out"
                           borderWidth="2px"
                           borderColor={methodSelectedOption === option.id ? 'brand' : 'gray.600'}
                           bg={methodSelectedOption === option.id ? 'brand' : '#F5E87633'}
                           onClick={() => setMethodSelectedOption(option.id)}
                        >
                           <Text
                              color={methodSelectedOption === option.id ? 'black' : 'brand'}
                              fontWeight="600"
                              textStyle="xs"
                           >
                              {option.label}
                           </Text>
                        </Box>
                     ))}
                  </Flex>
               </Field.Root>
               <Field.Root>
                  <Field.Label textStyle="xs" color="white" mb="1">
                     Are you in favour of the UK granting patients the right to grow their own medicine?
                  </Field.Label>
                  <Flex justify="start" flexWrap="wrap" gap="15px">
                     {options2.map(option2 => (
                        <Box
                           key={option2.id}
                           cursor="pointer"
                           borderRadius="12px"
                           minW="130px"
                           h="10"
                           px="6"
                           display="flex"
                           alignItems="center"
                           justifyContent="center"
                           textAlign="center"
                           transition="all 0.3s ease-in-out"
                           borderWidth="2px"
                           borderColor={medicineSelectedOption === option2.id ? 'brand' : 'gray.600'}
                           bg={medicineSelectedOption === option2.id ? 'brand' : '#F5E87633'}
                           onClick={() => setMedicineSelectedOption(option2.id)}
                        >
                           <Text
                              color={medicineSelectedOption === option2.id ? 'black' : 'brand'}
                              fontWeight="600"
                              textStyle="xs"
                           >
                              {option2.label}
                           </Text>
                        </Box>
                     ))}
                  </Flex>
               </Field.Root>
               <Field.Root>
                  <Field.Label textStyle="xs" color="white" mb="1">
                     What is the best consumption method for you?
                  </Field.Label>
                  <Box css={signupStyles.multiselect_box_con}>
                     {/* Multi-select dropdown */}
                     <Select.Root
                        collection={collection}
                        height="full"
                        width="full"
                        position="absolute"
                        top="0"
                        left="0"
                        zIndex="9"
                        multiple
                        onValueChange={handleSelection}
                        value={selectedPokemons}
                     >
                        <Select.HiddenSelect />
                        <Select.Control style={{ minHeight: '100%' }}>
                           <Select.Trigger style={{ height: '100%', opacity: 0 }}>
                              <Select.ValueText placeholder="Select Pokémon" />
                           </Select.Trigger>
                           <Select.IndicatorGroup style={{ height: '100%' }}>
                              {loading && <Spinner size="xs" borderWidth="1.5px" color="fg.muted" />}
                              <Select.Indicator />
                           </Select.IndicatorGroup>
                        </Select.Control>
                        <Portal>
                           <Select.Positioner css={signupStyles.multiselect_box_dropdown_con}>
                              <Select.Content>
                                 {collection.items.map(pokemonName => (
                                    <Select.Item key={pokemonName} item={pokemonName}>
                                       {pokemonName} {/* Directly use pokemonName (it's already a string) */}
                                       <Select.ItemIndicator />
                                    </Select.Item>
                                 ))}
                              </Select.Content>
                           </Select.Positioner>
                        </Portal>
                     </Select.Root>

                     {/* Display selected Pokémon as tags */}
                     <Box css={signupStyles.multiselect_box}>
                        {selectedPokemons.length === 0 ? (
                           <span style={{ color: 'white', fontSize: '14px', paddingLeft: '5px', paddingTop: '3px' }}>
                              Select
                           </span>
                        ) : (
                           selectedPokemons.map(pokemonName => (
                              <span
                                 key={pokemonName}
                                 style={{
                                    background: 'darkGrey',
                                    borderRadius: '12px',
                                    fontSize: '12px',
                                    padding: '2px 5px',
                                    color: 'black',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '2px',
                                 }}
                              >
                                 {pokemonName}
                                 <button
                                    onClick={() => handleRemovePokemon(pokemonName)}
                                    style={{ zIndex: 99, cursor: 'pointer' }}
                                 >
                                    <AiOutlineClose />
                                 </button>
                              </span>
                           ))
                        )}
                     </Box>
                  </Box>
               </Field.Root>

               <Box css={signupStyles.signup_btn_con}>
                  <Button
                     onClick={previous}
                     bg="darkerBtn"
                     textStyle="xs"
                     color="white"
                     h="10"
                     maxW="36"
                     minW="36"
                     p="0"
                  >
                     Back
                  </Button>
                  <Button textStyle="xs" h="10" maxW="36" minW="36" p="0">
                     Next
                  </Button>
               </Box>
            </Stack>
         </Box>
      </>
   );
};

export default Survey;
